import { FormStateType } from "src/forms/types";
import { SiteDetailsInput } from "@amzn/ttechclaimintakeservice-client";

/**
 * Format date from yyyy-MM-dd to yyyy/MM/dd format for backend API.
 * @param date - Date string in yyyy-MM-dd format
 * @returns Date string in yyyy/MM/dd format, or undefined if input is undefined
 */
export const formatDateForBackend = (date: string | undefined): string | undefined => {
  if (!date) return undefined;
  // Convert yyyy-MM-dd to yyyy/MM/dd
  return date.replace(/-/g, "/");
};

/**
 * Build address object from individual field values.
 * Returns undefined if all address fields are undefined.
 * @param addressLine1 - Address line 1
 * @param addressLine2 - Address line 2
 * @param city - City
 * @param country - Country
 * @returns SiteDetailsInput object or undefined if all fields are undefined
 */
export const buildAddressObject = (
  addressLine1: string | undefined,
  addressLine2: string | undefined,
  city: string | undefined,
  country: string | undefined
): SiteDetailsInput | undefined => {
  // Return undefined if all fields are empty
  if (!addressLine1 && !addressLine2 && !city && !country) {
    return undefined;
  }

  return {
    addressLine1,
    addressLine2,
    city,
    country,
  };
};

/**
 * Safely get form field value from form state.
 * Handles nested value objects and converts empty strings to undefined.
 * @param formState - The form state object
 * @param fieldName - The name of the field to retrieve
 * @returns The field value as a string, or undefined if not found or empty
 */
export const getFieldValue = (formState: FormStateType, fieldName: string): string | undefined => {
  const field = formState[fieldName];
  if (!field) {
    return undefined;
  }
  const value = field.value?.value ?? field.value ?? "";
  // Convert empty strings to undefined for optional fields
  return value === "" ? undefined : value;
};

/**
 * Safely get boolean field value from form state.
 * Converts "Yes"/"No" string values to boolean.
 * @param formState - The form state object
 * @param fieldName - The name of the field to retrieve
 * @returns Boolean value (true for "Yes", false for "No"), or undefined if not found or empty
 */
export const getBooleanFieldValue = (
  formState: FormStateType,
  fieldName: string
): boolean | undefined => {
  const value = getFieldValue(formState, fieldName);
  if (!value) return undefined;
  return value === "Yes";
};

/**
 * Build attachments array from form state field.
 * Extracts file metadata (extension, S3 key, name, size, description) from uploaded files.
 * @param formState - The form state object
 * @param fieldName - The name of the file upload field
 * @returns Array of attachment objects, or undefined if no attachments
 */
export const buildAttachments = (formState: FormStateType, fieldName: string) => {
  const field = formState[fieldName];
  if (!field || !field.value || !Array.isArray(field.value)) {
    return undefined;
  }

  const attachments = field.value.map((file: any) => ({
    fileExtension: file.fileExtension,
    s3Key: file.s3Key,
    fileName: file.fileName,
    fileSize: file.fileSize,
    fileDescription: file.fileDescription,
  }));

  return attachments.length > 0 ? attachments : undefined;
};
